import { useInvalidateUseGetAutopilotTasksForCampaign } from '@urbanx/agx.activities.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  type ContractDocuments,
  DocumentTypes,
  type StaffUpdateContractTerminationRequest,
  StaffUpdateContractTerminationRequestContractTerminationReasons,
  StaffUpdateContractTerminationRequestDepositEntitlement,
  useInvalidateUseStaffGetContract,
  useStaffUpdateContractTermination,
} from '@urbanx/agx.campaigns.client';
import type { UploadedDocument } from '@urbanx/agx.campaigns.client';
import type { RadioGroupOptions } from '@urbanx/agx.ui-components2';
import type { AxiosProgressEvent, GenericAbortSignal } from 'axios';
import { useSelectedCampaign } from 'hooks';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { uploadFile } from 'services';

const DEPOSIT_OPTIONS: RadioGroupOptions[] = [
  {
    label: 'Buyer',
    value: StaffUpdateContractTerminationRequestDepositEntitlement.Buyer,
  },
  {
    label: 'Vendor',
    value: StaffUpdateContractTerminationRequestDepositEntitlement.Vendor,
  },
  {
    label: 'No deposit received',
    value: StaffUpdateContractTerminationRequestDepositEntitlement.NoDepositReceived,
  },
];

const REASON_OPTIONS: RadioGroupOptions[] = [
  {
    label: 'Cooling off period',
    value: StaffUpdateContractTerminationRequestContractTerminationReasons.CoolingOffPeriod,
  },
  {
    label: 'Failed Finance',
    value: StaffUpdateContractTerminationRequestContractTerminationReasons.FailedFinance,
  },
  {
    label: 'Failed Building & Pest',
    value: StaffUpdateContractTerminationRequestContractTerminationReasons.FailedBuildingAndPest,
  },
  {
    label: 'Special Conditions',
    value: StaffUpdateContractTerminationRequestContractTerminationReasons.SpecialConditions,
  },
];

interface UseContractTerminateProps {
  isOpen: boolean;
  setIsOpen: (_value: boolean) => void;
  onContractTerminated?: () => void;
}

export const useContractTerminate = (props: UseContractTerminateProps) => {
  const { onContractTerminated, isOpen, setIsOpen } = props;
  const { campaign: selectedCampaign } = useSelectedCampaign();
  const { contractId } = useParams();
  const [submitting, setIsSubmitting] = useState(false);
  const { getAuthToken } = useContext(AuthContext);
  const [selectedReason, setSelectedReason] = useState<
    StaffUpdateContractTerminationRequestContractTerminationReasons | undefined
  >(undefined);
  const [selectedDepositOption, setSelectedDepositOption] = useState<
    StaffUpdateContractTerminationRequestDepositEntitlement | undefined
  >(undefined);
  const [selectedTerminationDate, setSelectedTerminationDate] = useState<Date | null>(new Date());
  const [vendorTerminationConfirmationFiles, setVendorTerminationConfirmationFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const [buyerTerminationConfirmationFiles, setBuyerTerminationConfirmationFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();
  const invalidateUseStaffGetContract = useInvalidateUseStaffGetContract();
  const { staffUpdateContractTermination } = useStaffUpdateContractTermination({
    onSuccess: () => {
      invalidateTasks(selectedCampaign?.campaignId ?? '', contractId);
      if (contractId) {
        invalidateUseStaffGetContract(contractId);
      }
    },
    onError: () => {},
  });

  const buildRenamedFormData = (formData: FormData) => {
    const file = formData.get('File');
    if (!file || typeof File === 'undefined' || !(file instanceof File)) {
      return formData;
    }

    const renamedFile = new File([file], `${contractId}/${file.name}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
    const renamedFormData = new FormData();
    renamedFormData.append('File', renamedFile);
    return renamedFormData;
  };

  const onUploadFile = async (
    authToken: string,
    file: string | FormData,
    uploadCampaignId: string,
    uploadedFormType: DocumentTypes,
    uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    abortSignal?: GenericAbortSignal,
    uploadContractId?: string
  ) => {
    try {
      const renamedFormData =
        typeof FormData !== 'undefined' && file instanceof FormData
          ? buildRenamedFormData(file)
          : file;

      return await uploadFile(
        authToken,
        renamedFormData,
        uploadCampaignId,
        uploadedFormType,
        uploadProgress ?? null,
        abortSignal ?? null,
        undefined,
        uploadContractId
      );
    } catch {
      return undefined;
    }
  };

  const uploadSelectedFile = async (
    authToken: string,
    uploadCampaignId: string,
    documentType: DocumentTypes,
    selectedFile?: UploadedDocument | File,
    uploadContractId?: string
  ) => {
    if (!selectedFile || !(selectedFile instanceof File)) return selectedFile;

    const formData = new FormData();
    formData.append('File', selectedFile);

    return onUploadFile(
      authToken,
      formData,
      uploadCampaignId,
      documentType,
      undefined,
      undefined,
      uploadContractId
    );
  };

  const onTerminateConfirm = async () => {
    if (
      !selectedCampaign?.campaignId ||
      !contractId ||
      !selectedTerminationDate ||
      !selectedDepositOption ||
      !selectedReason ||
      vendorTerminationConfirmationFiles.length === 0 ||
      buyerTerminationConfirmationFiles.length === 0
    ) {
      return;
    }

    setIsSubmitting(true);

    try {
      const authToken = await getAuthToken();
      if (!authToken) return;

      const vendorTerminationConfirmation = await uploadSelectedFile(
        authToken,
        selectedCampaign.campaignId,
        DocumentTypes.VendorTerminationConfirmation,
        vendorTerminationConfirmationFiles[0],
        contractId
      );

      const buyerTerminationConfirmation = await uploadSelectedFile(
        authToken,
        selectedCampaign.campaignId,
        DocumentTypes.BuyerTerminationConfirmation,
        buyerTerminationConfirmationFiles[0],
        contractId
      );

      if (!vendorTerminationConfirmation || !buyerTerminationConfirmation) {
        return;
      }

      const contractDocuments: ContractDocuments = {
        vendorTerminationConfirmation,
        buyerTerminationConfirmation,
      };

      const body = {
        terminationDate: selectedTerminationDate,
        depositEntitlement: selectedDepositOption,
        contractTerminationReasons: selectedReason,
        contractDocuments,
      } as StaffUpdateContractTerminationRequest & {
        contractDocuments: ContractDocuments;
      };

      await staffUpdateContractTermination({
        campaignId: selectedCampaign.campaignId,
        contractId,
        body,
      });

      setIsOpen(false);
      onContractTerminated?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const onTerminateSuccess = () => {
    onContractTerminated?.();
  };

  const onDateChange = (date: Date | null) => {
    setSelectedTerminationDate(date);
  };

  const onVendorTerminationConfirmationChanged = ({
    value,
  }: {
    value?: (UploadedDocument | File)[];
  }) => {
    setVendorTerminationConfirmationFiles(value ?? []);
  };

  const onBuyerTerminationConfirmationChanged = ({
    value,
  }: {
    value?: (UploadedDocument | File)[];
  }) => {
    setBuyerTerminationConfirmationFiles(value ?? []);
  };

  const onDepositOptionChange = (value: string) => {
    setSelectedDepositOption(value as StaffUpdateContractTerminationRequestDepositEntitlement);
  };

  const onReasonChange = (value: string) => {
    setSelectedReason(value as StaffUpdateContractTerminationRequestContractTerminationReasons);
  };

  const canSubmit =
    selectedTerminationDate != null &&
    vendorTerminationConfirmationFiles.length > 0 &&
    buyerTerminationConfirmationFiles.length > 0 &&
    selectedReason != null &&
    selectedDepositOption != null &&
    !submitting;

  useEffect(() => {
    if (isOpen) {
      setSelectedTerminationDate((currentValue) => currentValue ?? new Date());
      return;
    }

    setSelectedDepositOption(undefined);
    setSelectedReason(undefined);
    setSelectedTerminationDate(null);
    setVendorTerminationConfirmationFiles([]);
    setBuyerTerminationConfirmationFiles([]);
    setIsSubmitting(false);
  }, [isOpen]);

  return {
    onTerminateSuccess,
    onTerminateConfirm,
    depositOptions: DEPOSIT_OPTIONS,
    reasonOptions: REASON_OPTIONS,
    onDateChange,
    canSubmit,
    selectedDepositOption,
    selectedReason,
    selectedTerminationDate,
    vendorTerminationConfirmationFiles,
    buyerTerminationConfirmationFiles,
    onVendorTerminationConfirmationChanged,
    onBuyerTerminationConfirmationChanged,
    onDepositOptionChange,
    onReasonChange,
    onUploadFile,
    getAuthToken,
    selectedCampaign,
    submitting,
  };
};
